import React, { useState, useReducer, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  Select,
  message,
  Row,
  Col,
  DatePicker,
} from "antd";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { createTransaction } from "../../../Services/TransactionService";
import TransactionCreationForm from "../../elements/Forms/TransactionCreationForm/TransactionCreationForm";
import BulkInputForm from "../../elements/Forms/BulkInputForm/BulkInputForm";
import {
  FETCH_ALL_DISCOUNT_API,
  FETCH_ALL_STOCK_API,
  FETCH_ALL_TRANSACTION_API,
} from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";
import { checkInternetBeforeAPI } from "../../../Utils/NetworkUtils";
import dayjs from "dayjs";

import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";

const TransactionCreationPageEcom = ({ history, userData }) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [modalForm] = Form.useForm();
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [barcodeValue, setBarcodeValue] = useState(""); // Store barcode value
  const [scannerActive, setScannerActive] = useState(false); // Track if the scanner is active
  const [transactionDate, setTransactionDate] = useState(null); // Will be set by DatePicker
  const [noSaleDate, setNoSaleDate] = useState(null); // Separate date for No Sale transactions
  const [submitting, setSubmitting] = useState(false);

  // Optional toggle: block oversell outright
  const BLOCK_OVERSELL = false;

  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // Handle barcode scanning result

  // replace handleScanBarcode
  const handleScanBarcode = (err, result) => {
    if (!result) return;
    const scanned = normalize(result.text);
    if (!isLikelyBarcode(scanned)) return; // ignore noise/partials

    if (stockByBarcode.has(scanned)) {
      modalForm.setFieldsValue({ barcode: scanned });
      modalForm.validateFields(["barcode"]);
      setScannerActive(false); // stop scanner
    } else {
      message.error("Barcode not found in stock!");
    }
  };

  // stop scanner when modal closes
  useEffect(() => {
    if (!isModalVisible) setScannerActive(false);
  }, [isModalVisible]);

  const {
    fetchedData: { data: discountData },
  } = useFetch(`${FETCH_ALL_DISCOUNT_API}`, state);

  const {
    fetchedData: { data: stockData },
  } = useFetch(
    `${FETCH_ALL_STOCK_API}/cc/${userData.counterCode}/dropdown`,
    state
  );

  const {
    fetchedData: { data: historyTransactionData },
  } = useFetch(`${FETCH_ALL_TRANSACTION_API}/recent`, state);
  // Handle manual input for barcode
  const handleManualInputChange = (e) => {
    setBarcodeValue(e.target.value);
  };

  const normalize = (v) => (v ?? "").toString().trim();
  const isLikelyBarcode = (v) => /^[A-Za-z0-9._-]{6,}$/.test(normalize(v));

  // Build maps once stockData changes
  const stockByBarcode = React.useMemo(() => {
    const m = new Map();
    (stockData || []).forEach(
      (s) => s?.barcode && m.set(normalize(s.barcode), s)
    );
    return m;
  }, [stockData]);

  // Sum available stock for a barcode (status:'in stock')
  const totalAvailable = React.useCallback(
    (barcode) => {
      const bc = normalize(barcode);
      return (stockData || [])
        .filter((i) => normalize(i.barcode) === bc && i.status === "in stock")
        .reduce((sum, i) => sum + Number(i.currentQuantity || 0), 0);
    },
    [stockData]
  );

  // Handle date change
  const handleDateChange = (date) => {
    setTransactionDate(date);
  };

  // Bulk input is now handled directly by the BulkInputForm component
  const ready = Boolean(userData?.counterCode);
  const stockReady = ready && Array.isArray(stockData);
  // Add or Edit Item
  const upsertItem = React.useCallback(
    (list, newItem, editIndex = null) => {
      const qty = Math.max(1, parseInt(newItem.quantity, 10) || 0);
      const barcode = normalize(newItem.barcode);
      const avail = totalAvailable(barcode);
      const isBermasalah = qty > avail;

      // if edit, replace in place
      if (editIndex !== null) {
        const copy = [...list];
        copy[editIndex] = { ...newItem, barcode, quantity: qty, isBermasalah };
        return copy;
      }

      // merge by barcode
      const idx = list.findIndex((it) => normalize(it.barcode) === barcode);
      if (idx >= 0) {
        const copy = [...list];
        const mergedQty = Math.max(
          1,
          (parseInt(copy[idx].quantity, 10) || 0) + qty
        );
        copy[idx] = {
          ...copy[idx],
          ...newItem,
          barcode,
          quantity: mergedQty,
          isBermasalah: mergedQty > avail,
        };
        return copy;
      }
      return [...list, { ...newItem, barcode, quantity: qty, isBermasalah }];
    },
    [totalAvailable]
  );

  const handleAddOrEditItem = () => {
    modalForm
      .validateFields()
      .then((values) => {
        const barcode = normalize(values.barcode);
        if (!isLikelyBarcode(barcode) || !stockByBarcode.has(barcode)) {
          message.error("Barcode invalid / not in stock");
          return;
        }
        setItems((prev) =>
          upsertItem(prev, { ...values, barcode }, editingItemIndex)
        );
        modalForm.resetFields();
        setIsModalVisible(false);
        setEditingItemIndex(null);
        setBarcodeValue("");
      })
      .catch(() => {});
  };

  // Open Modal for Editing Item
  const handleEditItem = (index) => {
    setEditingItemIndex(index);
    modalForm.setFieldsValue(items[index]); // Populate the modal form with the item data
    setBarcodeValue(items[index].barcode); // Set barcode value for editing
    setIsModalVisible(true);
  };

  // Delete Item
  const handleDeleteItem = (index) => {
    setItems(items.filter((item, itemIndex) => itemIndex !== index));
    message.success("Item deleted successfully");
  };

  // Submit Main Form

  const handleNoSale = async () => {
    const { canProceed, message: errMsg } = await checkInternetBeforeAPI();
    if (!canProceed) return message.error(errMsg);

    const selectedDate = noSaleDate
      ? dayjs(noSaleDate).format("YYYY-MM-DD")
      : null;
    if (!selectedDate) return message.error("Please select a No Sale date");

    if ((items || []).length) {
      return message.error("Clear items before posting NO SALE.");
    }

    const existingForDay = historyTransactionData.find(
      (txn) => txn.transactionDate === selectedDate
    );
    if (existingForDay)
      return message.error("A transaction already exists for this date!");

    try {
      await createTransaction([
        {
          noBon: "NoSale",
          barcode: "NoSale",
          marketplace: null,
          quantity: 0,
          price: 0,
          soldBy: userData.name,
          counterCode: userData.counterCode,
          dept: userData.dept,
          total: 0,
          transactionDate: selectedDate,
        },
      ]);
      message.success("No Sale submitted");
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to submit No Sale.";
      message.error(msg);
    }
  };

  const handleNoSaleConfirmation = () => {
    Modal.confirm({
      title: "Confirm No Sale",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to perform a NO SALE action?</p>
          <DatePicker
            style={{ width: "100%", marginTop: 10 }}
            onChange={setNoSaleDate}
            disabledDate={(current) => {
              const now = dayjs().endOf("day");
              const fiveDaysAgo = dayjs().subtract(30, "day").startOf("day");
              return (
                current &&
                (current.isBefore(fiveDaysAgo) || current.isAfter(now))
              );
            }}
          />
        </div>
      ),
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: handleNoSale,
    });
  };

  const handleFormSubmit = async () => {
    try {
      const { canProceed, message: errMsg } = await checkInternetBeforeAPI();
      if (!canProceed) return message.error(errMsg);

      const values = await form.validateFields();
      const selectedDate = values.transactionDate?.format("YYYY-MM-DD");

      // NoSale exists for that date?
      const existingNoSale = historyTransactionData.find(
        (txn) =>
          txn.transactionDate === selectedDate && txn.barcode === "NoSale"
      );
      if (existingNoSale) {
        return message.error(
          "Tidak bisa membuat transaksi — NoSale sudah ada di hari tersebut."
        );
      }
      if (!items.length)
        return message.error("You must add at least one item!");

      // Build payload
      const payload = items.map((it) => {
        const bc = normalize(it.barcode);
        const stockItem = stockByBarcode.get(bc);
        const hpp = stockItem
          ? Number(stockItem.price) || 0
          : Number(it.price) || 0;
        const qty = Math.max(1, parseInt(it.quantity, 10) || 0);
        const avail = totalAvailable(bc);
        const isBermasalah = qty > avail;

        return {
          noBon: values.noBon,
          barcode: bc,
          marketplace: values.marketplace,
          sku: it.sku,
          discount: Number(it.discount) || 0,
          quantity: qty,
          price: Number(it.price) || 0,
          hpp,
          keterangan: it.keterangan || "",
          soldBy: userData.name,
          counterCode: userData.counterCode,
          dept: userData.dept,
          isBermasalah,
          article: it.article,
          color: it.color,
          total: Number(it.total) || (Number(it.price) || 0) * qty,
          transactionDate: selectedDate,
          ...(isBermasalah ? { bR: 1 } : {}),
        };
      });

      if (BLOCK_OVERSELL && payload.some((p) => p.isBermasalah)) {
        return message.error("Some items exceed available stock.");
      }

      setSubmitting(true);

      // Optional: idempotency key (same request won’t be double-applied server-side if supported)
      // const idemKey = btoa(JSON.stringify(payload)).slice(0, 64);
      // await createTransaction(payload, { headers: { "Idempotency-Key": idemKey } });

      await createTransaction(payload);
      message.success("Transaction submitted successfully!");
      form.resetFields();
      setItems([]);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to submit transaction. Please try again.";
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Barcode",
      dataIndex: "barcode",
      key: "barcode",
    },

    {
      title: "Diskon",
      dataIndex: "discount",
      key: "discount",
      render: (value) => `${value}%`,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Harga",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record, index) => (
        <div>
          <Button
            onClick={() => handleEditItem(index)}
            style={{ marginRight: "10px" }}
          >
            Edit
          </Button>
          <Button onClick={() => handleDeleteItem(index)} danger>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        width: "100vw",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Main Form */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Buat Transaksi (MRI)</h2>

        <div style={{ width: "100%", padding: "0 10px" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormSubmit}
            initialValues={{
              noBon: "",
              transactionDate: null,
              marketplace: null,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="No Bon"
                  name="noBon"
                  rules={[{ required: true, message: "Please input No Bon!" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Tanggal Transaksi"
                  name="transactionDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select Tanggal Transaksi!",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    onChange={handleDateChange}
                    disabledDate={(current) => {
                      const now = dayjs().endOf("day");
                      const sevenDaysAgo = dayjs()
                        .subtract(30, "day")
                        .startOf("day");
                      return (
                        current &&
                        (current.isBefore(sevenDaysAgo) || current.isAfter(now))
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Marketplace"
                  name="marketplace"
                  rules={[
                    { required: true, message: "Please select a marketplace!" },
                  ]}
                >
                  <Select placeholder="Select marketplace">
                    <Select.Option value="Lazada">Lazada</Select.Option>
                    <Select.Option value="Shopee">Shopee</Select.Option>
                    <Select.Option value="TikTok">TikTok</Select.Option>
                    <Select.Option value="Tokopedia">Tokopedia</Select.Option>
                    <Select.Option value="Blibli">Blibli</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Card stack layout */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                margin: "16px 0",
              }}
            >
              {items.map((item, index) => (
                <div
                  key={`${normalize(item.barcode)}-${index}`}
                  style={{
                    padding: "12px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "16px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        {item.barcode}
                      </div>
                      <div
                        style={{
                          marginBottom: "12px",
                          color: "#666",
                        }}
                      >
                        {item.article}
                      </div>
                      <div
                        style={{
                          marginBottom: "12px",
                          color: "#666",
                        }}
                      >
                        {item.color} - {item.size}
                      </div>
                      {item.discount > 0 && (
                        <div
                          style={{
                            marginBottom: "12px",
                            color: "#666",
                          }}
                        >
                          <strong>Discount:</strong> {item.discount}%
                        </div>
                      )}
                      {item.keterangan && (
                        <div
                          style={{
                            marginBottom: "12px",
                            color: "#666",
                          }}
                        >
                          <strong>Notes:</strong> {item.keterangan}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "14px",
                        }}
                      >
                        <strong>Quantity:</strong> {item.quantity}
                      </div>
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "14px",
                        }}
                      >
                        <strong>Price:</strong> Rp. {item.price}
                      </div>
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "14px",
                        }}
                      >
                        <strong>Total:</strong> Rp. {item.total}
                      </div>
                    </div>
                  </div>
                  {item.keterangan && (
                    <div
                      style={{
                        marginBottom: "12px",
                        padding: "8px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "6px",
                      }}
                    >
                      <strong>Keterangan:</strong> {item.keterangan}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "12px",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => handleEditItem(index)}
                      style={{
                        marginRight: "8px",
                        minWidth: "80px",
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteItem(index)}
                      style={{
                        minWidth: "80px",
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <Button
                type="primary"
                onClick={() => setIsModalVisible(true)}
                disabled={!stockReady}
              >
                Tambahkan Barang
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setIsBulkModalVisible(true)}
                disabled={!stockReady}
              >
                Bulk Input
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
              >
                Submit
              </Button>
              <Button type="primary" onClick={handleNoSaleConfirmation} danger>
                NO SALE
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Modal for Adding/Editing Items */}
      <Modal
        title={editingItemIndex === null ? "Tambahkan Barang" : "Edit Item"}
        open={isModalVisible}
        onOk={handleAddOrEditItem}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingItemIndex(null); // Reset editing index
        }}
        okText={editingItemIndex === null ? "Add" : "Update"}
      >
        <TransactionCreationForm
          modalForm={modalForm}
          handleManualInputChange={handleManualInputChange}
          barcodeValue={barcodeValue}
          setScannerActive={setScannerActive}
          scannerActive={scannerActive}
          handleScanBarcode={handleScanBarcode}
          stockData={stockData}
          discountData={discountData}
          setBarcodeValue={setBarcodeValue}
          userData={userData}
        />
      </Modal>

      {/* Modal for Bulk Input */}
      <BulkInputForm
        isVisible={isBulkModalVisible}
        onCancel={() => setIsBulkModalVisible(false)}
        stockData={stockData}
        userData={userData}
      />
    </div>
  );
};

export default TransactionCreationPageEcom;
